import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const PRIMARY = '#F59E0B';
const SECONDARY = '#06B6D4';
const ACCENT = '#8B5CF6';
const BG_TOP = '#0B1120';
const BG_MID = '#0F1419';
const BG_BOT = '#08090c';

const TOTAL_FRAMES = 960;

const PHASE = {
  title: [0, 110] as const,
  define: [100, 250] as const,
  layers: [240, 500] as const,
  battery: [490, 700] as const,
  steps: [690, 840] as const,
  apply: [830, 960] as const,
};

function phaseOpacity(
  frame: number,
  enterStart: number,
  enterEnd: number,
  exitStart: number,
  exitEnd: number,
): number {
  const fadeIn = interpolate(frame, [enterStart, enterEnd], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  if (exitStart >= exitEnd) {
    return fadeIn;
  }
  const fadeOut = interpolate(frame, [exitStart, exitEnd], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return fadeIn * fadeOut;
}

const GlowOrb: React.FC<{ x: number; y: number; size: number; color: string; delay: number }> = ({
  x,
  y,
  size,
  color,
  delay,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const pulse = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 40 },
  });
  const drift = Math.sin(frame * 0.03 + delay * 0.1) * 12;
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y + drift,
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${color}55 0%, transparent 70%)`,
        filter: 'blur(28px)',
        opacity: 0.35 + pulse * 0.15,
        pointerEvents: 'none',
      }}
    />
  );
};

const DefinitionCard: React.FC<{ opacity: number; y: number }> = ({ opacity, y }) => (
  <div
    style={{
      position: 'absolute',
      left: 120,
      right: 120,
      top: 200 + y,
      opacity,
      transform: `translateY(${interpolate(opacity, [0, 1], [40, 0])}px)`,
    }}
  >
    <div
      style={{
        padding: '44px 56px',
        borderRadius: 28,
        background: `linear-gradient(145deg, rgba(245,158,11,0.12) 0%, rgba(6,182,212,0.08) 100%)`,
        border: `1px solid rgba(245,158,11,0.25)`,
        boxShadow: `0 0 80px rgba(139,92,246,0.15)`,
      }}
    >
      <h2 style={{ margin: 0, fontSize: 42, fontWeight: 800, color: PRIMARY }}>
        什么是第一性原理？
      </h2>
      <p
        style={{
          margin: '24px 0 0',
          fontSize: 30,
          lineHeight: 1.65,
          color: 'rgba(255,255,255,0.88)',
          fontWeight: 500,
        }}
      >
        不依赖类比与惯例，将问题拆解到<strong style={{ color: SECONDARY }}> 最基本、不可再分的事实 </strong>
        ，再从这些公理出发重新推导结论。它源自亚里士多德，却在工程与创业中被反复验证。
      </p>
      <div style={{ display: 'flex', gap: 20, marginTop: 32, flexWrap: 'wrap' }}>
        {['质疑假设', '还原要素', '重构路径'].map((t, i) => (
          <span
            key={t}
            style={{
              padding: '10px 22px',
              borderRadius: 999,
              background: `${ACCENT}22`,
              color: ACCENT,
              fontSize: 26,
              fontWeight: 600,
              border: `1px solid ${ACCENT}44`,
            }}
          >
            {i + 1}. {t}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const LayerPeelSvg: React.FC<{ progress: number; treeProgress: number }> = ({ progress, treeProgress }) => {
  const layers = [
    { label: '行业惯例', y: 100, w: 640, color: 'rgba(255,255,255,0.25)' },
    { label: '竞争对手报价', y: 260, w: 540, color: 'rgba(245,158,11,0.35)' },
    { label: '“电池历来很贵”', y: 420, w: 440, color: 'rgba(6,182,212,0.4)' },
  ];

  return (
    <svg width={1440} height={680} viewBox="0 0 1440 680" style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id="fp-root" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={PRIMARY} />
          <stop offset="100%" stopColor={ACCENT} />
        </linearGradient>
      </defs>
      {layers.map((L, i) => {
        const peel = interpolate(progress, [i * 0.22, i * 0.22 + 0.35], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const x = 350 + (1 - peel) * 100;
        const op = peel * 0.95;
        return (
          <g key={L.label} opacity={op} transform={`translate(${x - 350}, ${(1 - peel) * -30})`}>
            <rect
              x={400}
              y={L.y}
              width={L.w}
              height={96}
              rx={20}
              fill={L.color}
              stroke="rgba(255,255,255,0.15)"
              strokeWidth={1}
            />
            <text x={424} y={L.y + 58} fill="rgba(255,255,255,0.9)" fontSize={36} fontWeight={600}>
              {L.label}
            </text>
          </g>
        );
      })}
      <g opacity={treeProgress}>
        <line x1={720} y1={155} x2={720} y2={280} stroke={SECONDARY} strokeWidth={4} opacity={0.6} />
        <line x1={720} y1={280} x2={480} y2={385} stroke={SECONDARY} strokeWidth={4} opacity={0.6} />
        <line x1={720} y1={280} x2={720} y2={490} stroke={SECONDARY} strokeWidth={3} opacity={0.5} />
        <line x1={720} y1={280} x2={960} y2={385} stroke={SECONDARY} strokeWidth={4} opacity={0.6} />
        <line x1={480} y1={385} x2={400} y2={490} stroke={ACCENT} strokeWidth={3} opacity={0.5} />
        <line x1={480} y1={385} x2={560} y2={490} stroke={ACCENT} strokeWidth={3} opacity={0.5} />
        <line x1={960} y1={385} x2={880} y2={490} stroke={ACCENT} strokeWidth={3} opacity={0.5} />
        <line x1={960} y1={385} x2={1040} y2={490} stroke={ACCENT} strokeWidth={3} opacity={0.5} />
        {[
          { x: 720, y: 130, t: '表层假设' },
          { x: 720, y: 310, t: '要素拆解' },
          { x: 400, y: 530, t: '钴' },
          { x: 560, y: 530, t: '镍' },
          { x: 720, y: 530, t: '铝' },
          { x: 880, y: 530, t: '碳' },
          { x: 1040, y: 530, t: '隔膜' },
        ].map((n) => (
          <g key={n.t}>
            <rect
              x={n.x - 70}
              y={n.y - 30}
              width={140}
              height={60}
              rx={14}
              fill={n.y > 480 ? 'url(#fp-root)' : 'rgba(6,182,212,0.25)'}
              stroke={n.y > 480 ? PRIMARY : SECONDARY}
              strokeWidth={1.5}
            />
            <text
              x={n.x}
              y={n.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#fff"
              fontSize={26}
              fontWeight={700}
            >
              {n.t}
            </text>
          </g>
        ))}
        <text x={720} y={625} textAnchor="middle" fill={PRIMARY} fontSize={28} fontWeight={600}>
          根层：不可再分的物理与化学事实
        </text>
      </g>
    </svg>
  );
};

const BatteryBars: React.FC<{ anim: number }> = ({ anim }) => {
  const traditionalH = interpolate(anim, [0, 0.45], [0, 280], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const breakdown = [
    { label: '钴', pct: 0.18, color: ACCENT },
    { label: '镍', pct: 0.22, color: SECONDARY },
    { label: '铝壳', pct: 0.12, color: PRIMARY },
    { label: '电解液', pct: 0.15, color: '#22d3ee' },
    { label: '其他', pct: 0.33, color: 'rgba(255,255,255,0.35)' },
  ];
  let acc = 0;
  return (
    <div style={{ display: 'flex', gap: 80, alignItems: 'flex-end', justifyContent: 'center', marginTop: 40 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>类比思维</div>
        <div
          style={{
            width: 120,
            height: 300,
            background: 'rgba(255,255,255,0.06)',
            borderRadius: 16,
            position: 'relative',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: traditionalH,
              background: 'linear-gradient(180deg, #ef4444, #991b1b)',
              borderRadius: '0 0 14px 14px',
            }}
          />
        </div>
        <div style={{ marginTop: 12, fontSize: 26, color: '#fca5a5' }}>“市价如此”</div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>第一性原理</div>
        <div
          style={{
            width: 220,
            height: 300,
            background: 'rgba(255,255,255,0.06)',
            borderRadius: 16,
            position: 'relative',
            border: '1px solid rgba(255,255,255,0.1)',
            overflow: 'hidden',
          }}
        >
          {breakdown.map((b, i) => {
            const segStart = interpolate(anim, [0.2 + i * 0.1, 0.35 + i * 0.1], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            const h = 300 * b.pct * segStart;
            const bottom = acc * 300;
            acc += b.pct;
            return (
              <div
                key={b.label}
                style={{
                  position: 'absolute',
                  bottom,
                  left: 0,
                  right: 0,
                  height: h,
                  background: b.color,
                  transition: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {segStart > 0.85 && (
                  <span style={{ fontSize: 22, fontWeight: 700, color: '#fff', textShadow: '0 1px 4px #000' }}>
                    {b.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 12, fontSize: 26, color: PRIMARY }}>成本结构可优化</div>
      </div>
    </div>
  );
};

export const FirstPrinciplesThinkingAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleSpring = spring({
    frame,
    fps,
    config: { damping: 16, stiffness: 100 },
  });

  const opTitle = phaseOpacity(frame, PHASE.title[0], PHASE.title[0] + 24, PHASE.title[1] - 28, PHASE.title[1]);
  const opDefine = phaseOpacity(frame, PHASE.define[0] + 10, PHASE.define[0] + 35, PHASE.define[1] - 30, PHASE.define[1]);
  const opLayers = phaseOpacity(frame, PHASE.layers[0] + 8, PHASE.layers[0] + 32, PHASE.layers[1] - 25, PHASE.layers[1]);
  const opBattery = phaseOpacity(frame, PHASE.battery[0] + 8, PHASE.battery[0] + 30, PHASE.battery[1] - 28, PHASE.battery[1]);
  const opSteps = phaseOpacity(frame, PHASE.steps[0] + 8, PHASE.steps[0] + 28, PHASE.steps[1] - 20, PHASE.steps[1]);
  const opApply = phaseOpacity(frame, PHASE.apply[0] + 10, PHASE.apply[0] + 35, TOTAL_FRAMES, TOTAL_FRAMES);

  const layerPeelProg = interpolate(frame, [PHASE.layers[0] + 20, PHASE.layers[0] + 140], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const treeProg = interpolate(frame, [PHASE.layers[0] + 100, PHASE.layers[0] + 200], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const batteryAnim = interpolate(frame, [PHASE.battery[0] + 25, PHASE.battery[0] + 160], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const stepCards = [
    { title: '识别并列出假设', detail: '写下“向来如此”的前提，标记哪些是他人观点而非事实' },
    { title: '分解到基本事实', detail: '用物理学、化学、经济学等底层知识描述“无法再拆”的单元' },
    { title: '从公理重构方案', detail: '像搭乐高一样重新组合，寻找更优路径与成本结构' },
  ];

  const scenarios = [
    { t: 'SpaceX 可回收火箭', d: '从燃料与材料成本出发，而非“火箭一次性”惯例' },
    { t: '产品创新', d: '用户真正需要的功能 vs 竞品已有功能的简单拷贝' },
    { t: '职业选择', d: '技能复利与时间价值，而非单一薪资数字类比' },
    { t: '学习新领域', d: '核心概念图谱优先，而非碎片化笔记堆砌' },
  ];

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(165deg, ${BG_TOP} 0%, ${BG_MID} 45%, ${BG_BOT} 100%)`,
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: '#fff',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      <GlowOrb x={100} y={120} size={340} color={PRIMARY} delay={0} />
      <GlowOrb x={1500} y={600} size={400} color={ACCENT} delay={20} />
      <GlowOrb x={800} y={80} size={280} color={SECONDARY} delay={10} />

      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)`,
          backgroundSize: '48px 48px',
          opacity: 0.35,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'absolute',
          left: 80,
          top: 72,
          opacity: opTitle * titleSpring,
          transform: `translateY(${interpolate(titleSpring, [0, 1], [50, 0])}px)`,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 72,
            fontWeight: 900,
            letterSpacing: -1,
            background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY}, ${ACCENT})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          第一性原理思维
        </h1>
        <p style={{ margin: '14px 0 0', fontSize: 28, color: 'rgba(255,255,255,0.45)' }}>
          First Principles · 从根本事实重新发明轮子
        </p>
      </div>

      <DefinitionCard
        opacity={opDefine}
        y={interpolate(
          spring({ frame: frame - PHASE.define[0], fps, config: { damping: 14, stiffness: 90 } }),
          [0, 1],
          [30, 0],
        )}
      />

      <div
        style={{
          position: 'absolute',
          left: 80,
          top: 100,
          opacity: opLayers,
          width: 'calc(100% - 160px)',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <div>
          <h2 style={{ textAlign: 'center', fontSize: 38, fontWeight: 800, color: SECONDARY, marginBottom: 8 }}>
            层层剥离：从假设到根原理
          </h2>
          <p style={{ textAlign: 'center', fontSize: 28, color: 'rgba(255,255,255,0.45)', marginBottom: 20 }}>
            以动力电池成本为例 · 示意结构
          </p>
          <LayerPeelSvg progress={layerPeelProg} treeProgress={treeProg} />
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 100,
          right: 100,
          top: 240,
          opacity: opBattery,
        }}
      >
        <h2 style={{ textAlign: 'center', fontSize: 40, fontWeight: 800, color: PRIMARY, margin: '0 0 8px' }}>
          马斯克电池案例：类比 vs 拆解
        </h2>
        <p style={{ textAlign: 'center', fontSize: 28, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
          当把成本还原为原材料与工艺，优化空间才清晰可见（图示为教学简化）
        </p>
        <BatteryBars anim={batteryAnim} />
      </div>

      <div
        style={{
          position: 'absolute',
          left: 70,
          right: 70,
          top: 200,
          opacity: opSteps,
          display: 'flex',
          gap: 28,
          justifyContent: 'center',
        }}
      >
        {stepCards.map((s, i) => {
          const sProg = spring({
            frame: frame - PHASE.steps[0] - 15 - i * 12,
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={s.title}
              style={{
                flex: 1,
                maxWidth: 560,
                padding: '36px 32px',
                borderRadius: 24,
                background: `linear-gradient(160deg, rgba(139,92,246,0.15) 0%, rgba(6,182,212,0.08) 100%)`,
                border: `1px solid ${i === 1 ? PRIMARY + '55' : 'rgba(255,255,255,0.1)'}`,
                boxShadow: i === 1 ? `0 0 40px ${PRIMARY}22` : 'none',
                opacity: sProg,
                transform: `translateY(${interpolate(sProg, [0, 1], [40, 0])}px)`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  marginBottom: 18,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: ACCENT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    fontWeight: 900,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </div>
                <h3 style={{ margin: 0, fontSize: 32, fontWeight: 800, color: PRIMARY }}>{s.title}</h3>
              </div>
              <p style={{ margin: '16px 0 0', fontSize: 28, lineHeight: 1.55, color: 'rgba(255,255,255,0.78)' }}>
                {s.detail}
              </p>
            </div>
          );
        })}
      </div>

      <div
        style={{
          position: 'absolute',
          left: 70,
          right: 70,
          top: 220,
          opacity: opApply,
        }}
      >
        <h2 style={{ textAlign: 'center', fontSize: 40, fontWeight: 800, color: SECONDARY, marginBottom: 60 }}>
          适用场景
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 22,
          }}
        >
          {scenarios.map((sc, i) => {
            const g = spring({
              frame: frame - PHASE.apply[0] - 12 - i * 10,
              fps,
              config: { damping: 13, stiffness: 110 },
            });
            return (
              <div
                key={sc.t}
                style={{
                  padding: '28px 32px',
                  borderRadius: 20,
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid rgba(245,158,11,0.2)`,
                  opacity: g,
                  transform: `scale(${interpolate(g, [0, 1], [0.94, 1])})`,
                }}
              >
                <div style={{ fontSize: 32, fontWeight: 800, color: PRIMARY, marginBottom: 10 }}>{sc.t}</div>
                <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>{sc.d}</div>
              </div>
            );
          })}
        </div>
        <p
          style={{
            textAlign: 'center',
            marginTop: 36,
            fontSize: 28,
            color: 'rgba(255,255,255,0.4)',
          }}
        >
          第一性原理不是否定经验，而是让经验在事实之上被检验与超越
        </p>
      </div>
    </AbsoluteFill>
  );
};
